// Home's "Your three things" and "What VERA noticed", worked out from the person's real Planner, Outlook and calendar.
// Simple, explainable rules, so every item says where it came from. Nothing is invented.

import type { CalEvent, Mail, Task } from "./m365";

export type Pick = { text: string; why: string; ask: string };

const AUTOMATED = /no-?reply|do-?not-?reply|notifications?@|newsletter|mailer-daemon|alerts?@|updates?@|news@|marketing@|info@|support@|billing@|jobs?@/i;

/** An email from a real person, not a system or a newsletter. */
export const fromPerson = (m: Mail) => !AUTOMATED.test(m.from.email) && !AUTOMATED.test(m.from.name);

const dayName = (d: Date, now: Date) => {
  const a = new Date(now); a.setHours(0, 0, 0, 0);
  const b = new Date(d); b.setHours(0, 0, 0, 0);
  const diff = Math.round((b.getTime() - a.getTime()) / 86_400_000);
  return diff === 0 ? "today" : diff === 1 ? "tomorrow" : d.toLocaleDateString("en-US", { weekday: "long" });
};
const clock = (d: Date) => d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });

export function todayPicks({ tasks, events, mail, now = new Date() }: { tasks?: Task[] | null; events?: CalEvent[] | null; mail?: Mail[] | null; now?: Date }) {
  const open = (tasks ?? []).filter((t) => t.percent < 100);
  const overdue = open.filter((t) => t.due && new Date(t.due) < now).sort((a, b) => +new Date(a.due!) - +new Date(b.due!));
  const soon = open.filter((t) => t.due && new Date(t.due) >= now && +new Date(t.due) - +now < 2 * 86_400_000).sort((a, b) => +new Date(a.due!) - +new Date(b.due!));
  const upcoming = (events ?? []).filter((e) => new Date(e.start) > now).sort((a, b) => +new Date(a.start) - +new Date(b.start));
  const waiting = (mail ?? []).filter((m) => m.unread && fromPerson(m));

  const picks: Pick[] = [];
  for (const t of overdue) picks.push({ text: `Overdue: ${t.title}`, why: `Planner · ${t.plan}`, ask: `Help me with my overdue Planner task "${t.title}" in ${t.plan}. What's the quickest way to finish it or move it?` });
  for (const t of soon) picks.push({ text: `Due ${dayName(new Date(t.due!), now)}: ${t.title}`, why: `Planner · ${t.plan}`, ask: `Help me get my Planner task "${t.title}" in ${t.plan} done before it's due.` });
  if (upcoming[0]) {
    const e = upcoming[0];
    picks.push({ text: `Prepare for ${e.subject} at ${clock(new Date(e.start))}`, why: "Calendar", ask: `Prepare me for my ${clock(new Date(e.start))} meeting "${e.subject}" today: who's coming, what it's about, and anything related in my email.` });
  }
  for (const m of waiting) picks.push({ text: `Reply to ${m.from.name || m.from.email}: ${m.subject}`, why: "Outlook", ask: `Help me reply to the email from ${m.from.name || m.from.email} about "${m.subject}".` });

  const noticed: string[] = [];
  if (overdue.length) noticed.push(`${overdue.length} of your Planner tasks ${overdue.length === 1 ? "is" : "are"} overdue.`);
  if (waiting.length) noticed.push(`${waiting.length} unread email${waiting.length === 1 ? "" : "s"} from people ${waiting.length === 1 ? "is" : "are"} waiting on you.`);
  if (events) noticed.push(upcoming.length ? `${upcoming.length} more meeting${upcoming.length === 1 ? "" : "s"} today, the next at ${clock(new Date(upcoming[0].start))}.` : "Your calendar is clear for the rest of today.");
  if (tasks && !open.length) noticed.push("Nothing in Planner is assigned to you right now.");

  return { priorities: picks.slice(0, 3), noticed: noticed.slice(0, 3) };
}
